import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArchiveBoxIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  getBlogPosts,
  getBlogStats,
  getBlogCategories,
  deleteBlogPost,
  approveBlogPost,
  rejectBlogPost,
  archiveBlogPost,
  bulkUpdateStatus,
  bulkDelete,
  getPostsMissingImages,
  regenerateBlogImage,
  analyzeContentIssues,
  fixBlogContent,
  getAllBlogPostsForExport,
  getAllBlogAuthors,
  bulkReassignAuthor,
  type AnalyzeContentResponse,
  type FixContentResponse,
} from '@/services/blogApi';
import * as XLSX from 'xlsx';
import type { BlogPost, BlogStatus, BlogCategory, BlogStats, BlogAuthor } from '@/types/blog';

const STATUS_BADGES: Record<BlogStatus, { variant: 'gray' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  in_review: { variant: 'warning', label: 'In Review' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'info', label: 'Archived' },
};

export default function BlogPosts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<BlogStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Image regeneration state
  const [isRegeneratingImages, setIsRegeneratingImages] = useState(false);
  const [regenerationProgress, setRegenerationProgress] = useState({ current: 0, total: 0 });

  // Content fix state
  const [showContentFixModal, setShowContentFixModal] = useState(false);
  const [contentAnalysis, setContentAnalysis] = useState<AnalyzeContentResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [fixProgress, setFixProgress] = useState({ current: 0, total: 0 });
  const [fixResults, setFixResults] = useState<FixContentResponse | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Author reassignment state
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [selectedAuthorForReassign, setSelectedAuthorForReassign] = useState<string | null>(null);
  const authorDropdownRef = useRef<HTMLDivElement>(null);

  // Close author dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (authorDropdownRef.current && !authorDropdownRef.current.contains(event.target as Node)) {
        setShowAuthorDropdown(false);
        setSelectedAuthorForReassign(null);
      }
    }
    if (showAuthorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAuthorDropdown]);

  // Fetch posts
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['blogPosts', page, statusFilter, categoryFilter, search],
    queryFn: () =>
      getBlogPosts(
        page,
        20,
        statusFilter || undefined,
        categoryFilter || undefined,
        search || undefined
      ),
  });

  // Fetch stats
  const { data: stats } = useQuery<BlogStats>({
    queryKey: ['blogStats'],
    queryFn: getBlogStats,
  });

  // Fetch categories
  const { data: categories } = useQuery<BlogCategory[]>({
    queryKey: ['blogCategories'],
    queryFn: getBlogCategories,
  });

  // Fetch posts missing images
  const { data: postsMissingImages, refetch: refetchMissingImages } = useQuery({
    queryKey: ['postsMissingImages'],
    queryFn: getPostsMissingImages,
  });

  // Fetch all blog authors
  const { data: authors } = useQuery<BlogAuthor[]>({
    queryKey: ['allBlogAuthors'],
    queryFn: getAllBlogAuthors,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteBlogPost(id),
    onSuccess: () => {
      toast.success('Blog post deleted');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setShowDeleteModal(false);
      setSelectedPost(null);
    },
    onError: () => toast.error('Failed to delete post'),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async (id: string) => approveBlogPost(id),
    onSuccess: () => {
      toast.success('Blog post published');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to publish post';
      toast.error(message);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      rejectBlogPost(id, reason),
    onSuccess: () => {
      toast.success('Blog post rejected and moved to drafts');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setShowRejectModal(false);
      setSelectedPost(null);
      setRejectionReason('');
    },
    onError: () => toast.error('Failed to reject post'),
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => archiveBlogPost(id),
    onSuccess: () => {
      toast.success('Blog post archived');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
    },
    onError: () => toast.error('Failed to archive post'),
  });

  // Bulk actions
  const bulkPublishMutation = useMutation({
    mutationFn: async (ids: string[]) => bulkUpdateStatus(ids, 'published'),
    onSuccess: () => {
      toast.success(`Published ${selectedPosts.length} posts`);
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setSelectedPosts([]);
    },
    onError: () => toast.error('Failed to publish posts'),
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async (ids: string[]) => bulkUpdateStatus(ids, 'archived'),
    onSuccess: () => {
      toast.success(`Archived ${selectedPosts.length} posts`);
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setSelectedPosts([]);
    },
    onError: () => toast.error('Failed to archive posts'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => bulkDelete(ids),
    onSuccess: () => {
      toast.success(`Deleted ${selectedPosts.length} posts`);
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogStats'] });
      setSelectedPosts([]);
    },
    onError: () => toast.error('Failed to delete posts'),
  });

  // Bulk reassign author mutation
  const bulkReassignMutation = useMutation({
    mutationFn: async ({ postIds, authorId }: { postIds: string[]; authorId: string }) =>
      bulkReassignAuthor(postIds, authorId),
    onSuccess: (data) => {
      toast.success(`Reassigned ${data.updated_count} posts`);
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      setSelectedPosts([]);
      setShowAuthorDropdown(false);
      setSelectedAuthorForReassign(null);
    },
    onError: () => toast.error('Failed to reassign posts'),
  });

  // Handle regenerating missing images
  const handleRegenerateMissingImages = async () => {
    if (!postsMissingImages || postsMissingImages.length === 0) return;

    setIsRegeneratingImages(true);
    setRegenerationProgress({ current: 0, total: postsMissingImages.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < postsMissingImages.length; i++) {
      const post = postsMissingImages[i];
      setRegenerationProgress({ current: i + 1, total: postsMissingImages.length });

      try {
        // Add 90-second timeout to prevent hanging
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Image generation timed out after 90 seconds')), 90000)
        );

        const result = await Promise.race([
          regenerateBlogImage(
            post.id,
            post.featured_image_suggestion,
            `Featured image for ${post.title}`
          ),
          timeoutPromise
        ]);

        if (result.success) {
          successCount++;
          console.log(`✓ Generated image ${i + 1}/${postsMissingImages.length}: ${post.title}`);
        } else {
          errorCount++;
          console.error(`✗ Failed image ${i + 1}/${postsMissingImages.length}: ${post.title} - ${result.message}`);
        }
      } catch (err) {
        errorCount++;
        console.error(`✗ Error image ${i + 1}/${postsMissingImages.length}: ${post.title}`, err);
        // Continue to next image instead of getting stuck
      }
    }

    setIsRegeneratingImages(false);
    setRegenerationProgress({ current: 0, total: 0 });

    if (successCount > 0) {
      toast.success(`Regenerated ${successCount}/${postsMissingImages.length} images${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
    } else {
      toast.error('Failed to regenerate images. Check console for details.');
    }

    // Refresh data
    queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
    refetchMissingImages();
  };

  // Handle analyzing content issues
  const handleAnalyzeContent = async () => {
    setIsAnalyzing(true);
    setContentAnalysis(null);
    setFixResults(null);
    try {
      const result = await analyzeContentIssues(100);
      setContentAnalysis(result);
      setShowContentFixModal(true);
    } catch (err) {
      toast.error('Failed to analyze content issues');
      console.error(err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle fixing content issues
  const handleFixContent = async (blogIds: string[]) => {
    if (blogIds.length === 0) return;

    setIsFixing(true);
    setFixProgress({ current: 0, total: blogIds.length });
    setFixResults(null);

    // Process in batches of 3 to avoid timeout
    const batchSize = 3;
    const allResults: FixContentResponse['results'] = [];
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < blogIds.length; i += batchSize) {
      const batch = blogIds.slice(i, i + batchSize);
      setFixProgress({ current: i, total: blogIds.length });

      try {
        const result = await fixBlogContent(batch);
        allResults.push(...result.results);
        successCount += result.fixed;
        failCount += result.failed;
      } catch (err) {
        console.error('Batch fix error:', err);
        failCount += batch.length;
      }
    }

    setFixProgress({ current: blogIds.length, total: blogIds.length });
    setFixResults({
      success: true,
      mode: 'fix',
      dry_run: false,
      processed: blogIds.length,
      fixed: successCount,
      unchanged: allResults.filter(r => r.success && r.changes_made === 0).length,
      failed: failCount,
      results: allResults,
    });
    setIsFixing(false);

    if (successCount > 0) {
      toast.success(`Fixed ${successCount} blog posts`);
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
    }
    if (failCount > 0) {
      toast.error(`${failCount} posts failed to fix`);
    }
  };

  // Handle exporting blog topics to Excel
  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const posts = await getAllBlogPostsForExport();

      // Create worksheet data with headers
      const worksheetData = [
        ['Title', 'Category', 'Status', 'Slug', 'Created At'],
        ...posts.map(post => [
          post.title,
          post.category,
          post.status,
          post.slug,
          new Date(post.created_at).toLocaleDateString(),
        ]),
      ];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      ws['!cols'] = [
        { wch: 60 }, // Title
        { wch: 15 }, // Category
        { wch: 12 }, // Status
        { wch: 50 }, // Slug
        { wch: 15 }, // Created At
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Blog Topics');

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `blog-topics-${date}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);

      toast.success(`Exported ${posts.length} blog topics to ${filename}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export blog topics');
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<BlogPost>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
            }}
          >
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              readOnly
              className="rounded border-gray-300 cursor-pointer"
              aria-label="Select all posts"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              row.toggleSelected(!row.getIsSelected());
            }}
          >
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              readOnly
              className="rounded border-gray-300 cursor-pointer"
              aria-label={`Select post ${row.original.title}`}
            />
          </div>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const isPublished = row.original.status === 'published';

          return (
            <div className="max-w-md">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isPublished) {
                    // Open the public blog page in a new tab for published posts
                    const baseUrl = window.location.hostname === 'localhost'
                      ? 'http://localhost:8081'
                      : 'https://linkedbysix.com';
                    window.open(`${baseUrl}/blog/${row.original.slug}`, '_blank');
                  } else {
                    // Navigate to the admin preview page for drafts
                    navigate(`/blog/preview/${row.original.id}`);
                  }
                }}
                className="font-medium text-gray-900 truncate hover:text-brand hover:underline text-left"
                title={isPublished ? 'View on public site' : 'Preview draft'}
              >
                {row.original.title}
              </button>
              <div className="text-sm text-gray-500 truncate">
                /{row.original.slug}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const badge = STATUS_BADGES[status];
          const hasImage = !!row.original.featured_image_url?.trim();
          const hasAlt = !!row.original.featured_image_alt?.trim();
          const imageComplete = hasImage && hasAlt;

          return (
            <div className="flex items-center gap-2">
              <Badge variant={badge.variant} dot>
                {badge.label}
              </Badge>
              {row.original.ai_generated && (
                <SparklesIcon className="w-4 h-4 text-purple-500" title="AI Generated" />
              )}
              {/* Image status indicator */}
              {imageComplete ? (
                <div className="flex items-center" title="Image ready">
                  <PhotoIcon className="w-4 h-4 text-green-500" />
                </div>
              ) : (
                <div
                  className="flex items-center"
                  title={!hasImage ? 'Missing featured image' : 'Missing alt text'}
                >
                  <PhotoIcon className="w-4 h-4 text-amber-500" />
                  <ExclamationTriangleIcon className="w-3 h-3 text-amber-500 -ml-1" />
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'category_slug',
        header: 'Category',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600 capitalize">
            {row.original.category_slug?.replace(/-/g, ' ') || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'reading_time_minutes',
        header: 'Read Time',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {row.original.reading_time_minutes ?? 0} min
          </span>
        ),
      },
      {
        accessorKey: 'view_count',
        header: 'Views',
        cell: ({ row }) => (
          <span className="text-sm font-medium">
            {(row.original.view_count ?? 0).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                // Navigate to the admin preview page with edit capabilities
                navigate(`/blog/preview/${row.original.id}`);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Preview & Edit"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                navigate(`/blog/${row.original.id}`);
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            {row.original.status === 'in_review' && (
              <>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    approveMutation.mutate(row.original.id);
                  }}
                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-gray-100 rounded"
                  title="Approve & Publish"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSelectedPost(row.original);
                    setShowRejectModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                  title="Reject"
                >
                  <XCircleIcon className="w-4 h-4" />
                </button>
              </>
            )}
            {row.original.status === 'published' && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  archiveMutation.mutate(row.original.id);
                }}
                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-gray-100 rounded"
                title="Archive"
              >
                <ArchiveBoxIcon className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setSelectedPost(row.original);
                setShowDeleteModal(true);
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [navigate, approveMutation, archiveMutation]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog Posts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage blog content and publication workflow
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export to Excel Button */}
          <Button
            variant="secondary"
            onClick={handleExportToExcel}
            disabled={isExporting}
            leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
          >
            {isExporting ? 'Exporting...' : 'Export Topics'}
          </Button>
          {/* Fix Content Issues Button */}
          <Button
            variant="secondary"
            onClick={handleAnalyzeContent}
            disabled={isAnalyzing}
            leftIcon={<ExclamationTriangleIcon className="w-4 h-4" />}
          >
            {isAnalyzing ? 'Analyzing...' : 'Fix Content Issues'}
          </Button>
          {/* Regenerate Missing Images Button */}
          {postsMissingImages && postsMissingImages.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleRegenerateMissingImages}
              disabled={isRegeneratingImages}
              leftIcon={<PhotoIcon className="w-4 h-4" />}
            >
              {isRegeneratingImages
                ? `Regenerating... ${regenerationProgress.current}/${regenerationProgress.total}`
                : `Regenerate Images (${postsMissingImages.length})`}
            </Button>
          )}
          <Button
            onClick={() => navigate('/blog/new')}
            leftIcon={<PlusIcon className="w-4 h-4" />}
          >
            New Post
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold">{stats?.total_posts || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Drafts</p>
            <p className="text-2xl font-bold text-gray-600">{stats?.drafts || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">In Review</p>
            <p className="text-2xl font-bold text-orange-600">{stats?.in_review || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Published</p>
            <p className="text-2xl font-bold text-green-600">
              {stats?.live || 0}
              {(stats?.scheduled || 0) > 0 && (
                <span className="text-sm font-normal text-orange-500 ml-1">
                  ({stats?.scheduled} scheduled)
                </span>
              )}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Views</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats?.total_views?.toLocaleString() || 0}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as BlogStatus | '');
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="">All Statuses</option>
            <option value="draft">Drafts</option>
            <option value="in_review">In Review</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="">All Categories</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="blog-posts-search"
            name="blogPostsSearch"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search posts..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            aria-label="Search posts"
          />
        </div>

        {selectedPosts.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">
              {selectedPosts.length} selected
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bulkPublishMutation.mutate(selectedPosts)}
              loading={bulkPublishMutation.isPending}
            >
              Publish
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bulkArchiveMutation.mutate(selectedPosts)}
              loading={bulkArchiveMutation.isPending}
            >
              Archive
            </Button>
            {/* Author Reassignment Dropdown */}
            <div className="relative" ref={authorDropdownRef}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setShowAuthorDropdown(!showAuthorDropdown);
                  if (showAuthorDropdown) {
                    setSelectedAuthorForReassign(null);
                  }
                }}
                leftIcon={<UserCircleIcon className="w-4 h-4" />}
              >
                Assign Author
              </Button>
              {showAuthorDropdown && (
                <div className="absolute top-full mt-1 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[280px]">
                  {/* Author list */}
                  <div className="max-h-[250px] overflow-y-auto py-1">
                    {authors?.filter(a => a.is_active).length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        No active authors available
                      </div>
                    ) : (
                      authors?.filter(a => a.is_active).map((author) => (
                        <div key={author.id} className="relative group">
                          <button
                            type="button"
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                              selectedAuthorForReassign === author.id
                                ? 'bg-brand/10 text-brand'
                                : 'hover:bg-gray-100'
                            }`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedAuthorForReassign(author.id);
                            }}
                          >
                            {selectedAuthorForReassign === author.id && (
                              <CheckCircleSolidIcon className="w-4 h-4 text-brand flex-shrink-0" />
                            )}
                            <span className={`font-medium ${selectedAuthorForReassign === author.id ? '' : 'ml-6'}`}>
                              {author.name}
                            </span>
                            {author.expertise && author.expertise.length > 0 && (
                              <span className="text-xs text-gray-400">
                                ({author.expertise.slice(0, 2).join(', ')})
                              </span>
                            )}
                          </button>
                          {/* Bio tooltip on hover */}
                          <div className="hidden group-hover:block absolute left-full top-0 ml-2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-[60]">
                            <p className="font-medium text-sm mb-1">{author.name}</p>
                            <p className="text-gray-300 leading-relaxed">
                              {author.bio || 'No bio available'}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Confirm button */}
                  {selectedAuthorForReassign && (
                    <div className="border-t border-gray-200 p-2">
                      <Button
                        size="sm"
                        variant="primary"
                        className="w-full"
                        loading={bulkReassignMutation.isPending}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          bulkReassignMutation.mutate({
                            postIds: selectedPosts,
                            authorId: selectedAuthorForReassign,
                          });
                        }}
                      >
                        Assign to {authors?.find(a => a.id === selectedAuthorForReassign)?.name}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="danger"
              onClick={() => bulkDeleteMutation.mutate(selectedPosts)}
              loading={bulkDeleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={postsData?.posts || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={postsData?.total}
            onPageChange={setPage}
            getRowId={(row) => row.id}
            onRowSelectionChange={(rows) => {
              setSelectedPosts(rows.map((r) => r.original.id));
            }}
          />
        </CardBody>
      </Card>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPost(null);
        }}
        title="Delete Blog Post"
      >
        <p className="text-gray-600">
          Are you sure you want to delete "{selectedPost?.title}"? This action
          cannot be undone.
        </p>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => selectedPost && deleteMutation.mutate(selectedPost.id)}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedPost(null);
          setRejectionReason('');
        }}
        title="Reject Blog Post"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Rejecting "{selectedPost?.title}" will move it back to drafts.
          </p>
          <div>
            <label htmlFor="blog-post-rejection-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason *
            </label>
            <textarea
              id="blog-post-rejection-reason"
              name="rejectionReason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              placeholder="Please provide feedback for the author..."
              aria-label="Rejection reason"
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              selectedPost &&
              rejectMutation.mutate({
                id: selectedPost.id,
                reason: rejectionReason,
              })
            }
            loading={rejectMutation.isPending}
            disabled={!rejectionReason.trim()}
          >
            Reject
          </Button>
        </ModalFooter>
      </Modal>

      {/* Content Fix Modal */}
      <Modal
        isOpen={showContentFixModal}
        onClose={() => {
          if (!isFixing) {
            setShowContentFixModal(false);
            setContentAnalysis(null);
            setFixResults(null);
          }
        }}
        title="Fix Content Issues"
        size="lg"
      >
        <div className="space-y-4">
          {/* Analysis Results */}
          {contentAnalysis && !fixResults && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Content Issues Found</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Found <strong>{contentAnalysis.blogs_with_issues}</strong> AI-generated blogs
                      with "ask friends/family for recommendations" messaging that contradicts
                      Linked By Six's value proposition.
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                <p className="mb-2"><strong>What will be fixed:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>"Ask friends/family/neighbors for recommendations" → Removed</li>
                  <li>"Text three people you trust" → Removed</li>
                  <li>"Before searching online, reach out to..." → Removed</li>
                  <li>CTAs updated to mention Linked By Six as the automated solution</li>
                </ul>
              </div>

              {contentAnalysis.blogs_with_issues > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Title</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {contentAnalysis.analyses.slice(0, 20).map((blog) => (
                        <tr key={blog.blog_id}>
                          <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-xs">
                            {blog.title}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {blog.issues.length} issue{blog.issues.length !== 1 ? 's' : ''}
                          </td>
                        </tr>
                      ))}
                      {contentAnalysis.blogs_with_issues > 20 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-2 text-sm text-gray-500 italic">
                            ...and {contentAnalysis.blogs_with_issues - 20} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Fixing Progress */}
          {isFixing && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand"></div>
                <span className="text-gray-600">
                  Fixing content... {fixProgress.current}/{fixProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-brand h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(fixProgress.current / fixProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Fix Results */}
          {fixResults && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircleSolidIcon className="w-5 h-5 text-green-500" />
                  <span className="text-green-700 font-medium">{fixResults.fixed} fixed</span>
                </div>
                {fixResults.unchanged > 0 && (
                  <div className="text-gray-500">
                    {fixResults.unchanged} unchanged
                  </div>
                )}
                {fixResults.failed > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                    <span className="text-red-700">{fixResults.failed} failed</span>
                  </div>
                )}
              </div>

              {fixResults.results.filter(r => !r.success).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 mb-2">Failed to fix:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {fixResults.results.filter(r => !r.success).map((r) => (
                      <li key={r.blog_id}>{r.title}: {r.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowContentFixModal(false);
              setContentAnalysis(null);
              setFixResults(null);
            }}
            disabled={isFixing}
          >
            {fixResults ? 'Close' : 'Cancel'}
          </Button>
          {contentAnalysis && contentAnalysis.blogs_with_issues > 0 && !fixResults && (
            <Button
              onClick={() => handleFixContent(contentAnalysis.analyses.map(a => a.blog_id))}
              loading={isFixing}
              leftIcon={<SparklesIcon className="w-4 h-4" />}
            >
              Fix {contentAnalysis.blogs_with_issues} Posts
            </Button>
          )}
        </ModalFooter>
      </Modal>

    </div>
  );
}
